import { makeAutoObservable } from "mobx";
import { createStoreContext } from 'reusable/mobx-store-utils/storeUtils';
import variables from 'variables/variables';
import { downloadFile } from '@gap/utils/file/download-file'
import AuthStore from "auth/AuthStore";
import Loader from "reusable/mobx-helpers/Loader";
import { pdfClientSilent } from "api/trpc-pdf/trpc-client";
import { PdfRouter } from "@trpc-endpoints/trpc-pdf/routers/pdf-router";
import userCache from "cache/userCache";

interface PrintStoreParams {
   authStore: AuthStore
}

export default class PrintStore {
   sp
   contentNode: Node | undefined
   contentLoader = new Loader()
   downloadLoader = new Loader()
   private generatingList: string[] = []
   private generationPromises = new Map<string, Promise<string>>()

   constructor(sp: PrintStoreParams) {
      makeAutoObservable(this)
      this.sp = sp
   }

   get isGenerating() {
      return !!this.generatingList.length
   }

   generateWorkspaceDocumentIfNotExists = async (params: PdfRouter.WorkspaceDocumentSchema) => {
      return await this.generate(params.documentId, async () => {
         return await pdfClientSilent.generateWorkspaceDocumentIfNotExists.mutate(params)
      })
   }

   downloadCurrentPageAsPdf = async (docTitle: string) => {
      await this.downloadLoader.executeLoading(async () => {
         const pageUrlFragment = window.location.href.replaceAll(new RegExp(`^.*${variables.regionBaseUrl}`, 'g'), '')
         const searchParamString = new URLSearchParams({ documentName: docTitle }).toString()
         const accessToken = this.sp.authStore.accessToken
         const response = await fetch(`${variables.apiUrl}/pdf${pageUrlFragment}?${searchParamString}`, {
            method: 'GET',
            headers: {
               'Authorization': `Bearer ${accessToken}`,
            }
         })

         if (!response.ok) {
            throw new Error('Download failed')
         }

         const fileBlob = await response.blob()
         const fileName = `${this.sanitiseDocTitle(docTitle)}.pdf`

         downloadFile(fileBlob, fileName)
      })
   }

   /**
    * Wait for a generation with the given ID to complete.
    */
   waitForGeneration = async (id: string): Promise<string | null> => {
      return await this.downloadLoader.executeLoading(async () => {
         const existingPromise = this.generationPromises.get(id)
         if (existingPromise) {
            return await existingPromise
         }
         return null
      })
   }

   private generate = async (id: string, generateFunction: () => Promise<string>): Promise<string> => {
      if (this.generatingList.includes(id)) {
         // If already generating, remove from list and regenerate
         this.generatingList = this.generatingList.filter(x => x !== id)
         // Also remove the existing promise
         this.generationPromises.delete(id)
      }

      this.generatingList.push(id)

      // Create and store the promise
      const promise = generateFunction()
      this.generationPromises.set(id, promise)

      let result

      try {
         const isServiceUser = userCache.serviceUser.get()
         if (!isServiceUser) {
            result = await promise
         } else {
            result = ''
         }
      } finally {
         this.generatingList = this.generatingList.filter(x => x !== id)
         this.generationPromises.delete(id)
      }
      return result
   }

   private sanitiseDocTitle = (docTitle: string) => docTitle.replace(/[^a-zA-Z0-9-_\s]/g, '').replace(/\s+/g, ' ')
}

export const { Provider: PrintStoreProvider, useProvidedStore: usePrintStore } = createStoreContext(PrintStore)